import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '@components/forms/Input';
import Button from '@components/forms/Button';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import { useNavigate } from 'react-router-dom';
import { REQUEST_SELF_SIGNUP, post } from '@services';

const brokerAccessApplySchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address.')
    .required('Please enter your email.'),
  consent: yup
    .boolean()
    .oneOf([true], 'You must agree before submitting.')
    .required('You must agree before submitting.'),
}).required();

type BrokerAccessFormData = yup.InferType<typeof brokerAccessApplySchema>;

const GENERIC_ERROR_MESSAGE =
  'An error occurred while submitting your request. Please try again.';

const REGISTERED_EMAIL_ERROR_MESSAGE =
  'This email address is already registered with Authentik8. Please return to the login page and sign in, or use Forgot password if you’ve forgotten your password.';

const AUTHENTIK8_HOMEPAGE_URL = 'https://www.authentik8.com.au/';

const BrokerAccessApply = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showRegisteredEmailActions, setShowRegisteredEmailActions] = useState(false);
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<BrokerAccessFormData>({
    resolver: yupResolver(brokerAccessApplySchema),
    defaultValues: {
      email: '',
      consent: false,
    }
  });

  const email = watch('email');

  const onSubmit = async (data: BrokerAccessFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setShowRegisteredEmailActions(false);
    try {
      await post(
        REQUEST_SELF_SIGNUP,
        {
          email: data.email,
        },
        false
      );
      setIsSubmitted(true);
    } catch (error: any) {
      const isRegisteredEmailError =
        error?.response?.status === 400 &&
        error?.response?.data?.error?.code === 'InvalidUser';

      if (isRegisteredEmailError) {
        setErrorMessage(REGISTERED_EMAIL_ERROR_MESSAGE);
        setShowRegisteredEmailActions(true);
      } else {
        setErrorMessage(GENERIC_ERROR_MESSAGE);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('email', e.target.value);
  };

  const handleReturnHome = () => {
    window.location.assign(AUTHENTIK8_HOMEPAGE_URL);
  };

  // Success/Thanks page
  if (isSubmitted) {
    return (
      <div className="relative z-10 w-full max-w-[520px] mx-auto bg-white rounded-lg shadow-md px-8 py-10">
        <h2 className="text-2xl font-semibold mb-4 text-center text-grey-900">Thanks!</h2>
        <h3 className="text-2xl font-semibold mb-6 text-center text-grey-900">
          Check your email to get started
        </h3>
        <div className="text-sm text-grey-700 mb-4 text-left">
          <p className="mb-2">
            We've sent a secure link to verify your email and set up your Authentik8 profile.
          </p>
          <p>
            If you don't see it, check your spam folder.
          </p>
        </div>
        <div className="text-base font-bold text-grey-700 mb-6 text-left">
          <p>
            Need help?{' '}
            <a
              href="mailto:support@authentik8.com.au"
              className="text-black font-normal underline hover:opacity-80"
            >
              support@authentik8.com.au
            </a>
          </p>
        </div>
        <div className="flex flex-col justify-center mt-6">
          <Button
            type="button"
            onClick={handleReturnHome}
            className="w-full bg-primary text-white text-base font-semibold py-3 rounded-md hover:opacity-90 transition-opacity">
            Return to homepage
          </Button>
        </div>
      </div>
    );
  }

  // Form page
  return (
    <div className="relative z-10 w-full max-w-[520px] mx-auto bg-white rounded-lg shadow-md px-8 py-10">
      <h2 className="text-2xl mb-4 text-center text-grey-900 font-semibold">Broker access request</h2>
      <p className="text-sm text-grey-700 mb-6 text-left">
        Set up your Authentik8 access to create inspections for your lenders.
      </p>
      <h3 className="text-base font-semibold mb-3 text-left text-grey-900">Getting started</h3>
      <ol className="list-decimal list-outside pl-5 text-grey-700 text-sm mb-6 text-left leading-6 space-y-2">
        <li>
          Enter your work email address and agree to the Terms of Use.
        </li>
        <li>
          You'll receive an email with a secure link to set up your Authentik8 profile.
        </li>
        <li>
          Once your profile and MFA are complete, you can log in to request lender access.
        </li>
      </ol>
      <hr className="my-6 border-grey-300" />
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700 flex items-start">
              <span className="mr-2 flex-shrink-0 mt-0.5">
                <ErrorIcon />
              </span>
              <span>{errorMessage}</span>
            </p>
            {showRegisteredEmailActions && (
              <div className="mt-4 flex flex-wrap gap-4 pl-7">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-black underline hover:opacity-80"
                >
                  Return to login
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm font-medium text-black underline hover:opacity-80"
                >
                  Forgot password
                </button>
              </div>
            )}
          </div>
        )}
        <div className="space-y-4">
          <label className="flex flex-col">
            <span className="text-sm font-medium text-grey-700 mb-1 pb-1">Email</span>
            <Input
              {...register('email')}
              type="email"
              placeholder=""
              icon={errors.email && <ErrorIcon />}
              value={email}
              onChange={handleEmailChange}
            />
            <span className="text-xs text-grey-500 mt-1">
              Use your work email address so your lender can identify your request.
            </span>
            {errors.email && (
              <span className="text-error-500 text-2sm mt-[6px]">
                {errors.email.message}
              </span>
            )}
          </label>
        </div>

        <div className="mt-6">
          <label className="flex flex-col">
            <span className="text-sm font-medium text-grey-700 mb-1 pb-1">Consent</span>
            <div className="flex items-center gap-x-2">
              <input
                id="consent"
                type="checkbox"
                {...register('consent')}
                className="h-4 w-4 flex-shrink-0 rounded border-grey-300"
              />
              <label htmlFor="consent" className="text-base text-grey-700 cursor-pointer leading-tight">
                I agree to the{' '}
                <a
                  href="https://www.authentik8.com.au/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Terms of Use
                </a>
                {' '}and{' '}
                <a
                  href="https://www.valeur.io/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Privacy Policy
                </a>
                .
              </label>
            </div>
            {errors.consent && (
              <span className="text-error-500 text-2sm mt-[6px]">
                {errors.consent.message}
              </span>
            )}
          </label>
        </div>
        <div className="flex flex-col justify-center mt-6">
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary text-white text-base font-semibold py-3 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BrokerAccessApply;
